import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";
import { fmtTL, todayIso } from "@/lib/format";
import { CreditCard } from "lucide-react";

const METHODS = {
  havale: "Havale/EFT", cash: "Nakit", credit_card: "Kredi Kartı",
  check: "Çek/Senet", other: "Diğer",
};

export default function PaymentEntry() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({
    company_id: "",
    order_id: "none",
    payment_amount: "",
    receipt_date: todayIso(),
    payment_method: "havale",
    receipt_no: "",
    description: "",
    attachments: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);

  useEffect(() => {
    if (form.company_id) {
      api.get("/orders", { params: { company_id: form.company_id } })
         .then((r) => setOrders(r.data.filter(o => o.remaining_amount > 0 && o.status !== "cancelled")));
    }
  }, [form.company_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_id) return toast.error("Firma seçin");
    if (!form.payment_amount || Number(form.payment_amount) <= 0) return toast.error("Tutar girin");
    setSaving(true);
    try {
      await api.post("/payments", {
        company_id: form.company_id,
        order_id: form.order_id === "none" ? null : form.order_id,
        payment_amount: Number(form.payment_amount),
        receipt_date: form.receipt_date,
        payment_method: form.payment_method,
        receipt_no: form.receipt_no,
        description: form.description,
        attachment_url: (form.attachments || [])[0]?.download_url || "",
      });
      toast.success("Ödeme kaydedildi");
      navigate("/cari");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  const selectedCompany = companies.find((c) => c.id === form.company_id);

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-5">
      <div className="azd-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#16A34A]/10 text-[#16A34A] flex items-center justify-center">
            <CreditCard size={18} />
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-[#08285A]">Ödeme Girişi</h2>
            <p className="text-xs text-[#6B7280]">
              Dekont tarihi vade hesabında kullanılır
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Firma <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
              <SelectTrigger data-testid="payment-company"><SelectValue placeholder="Firma seçin" /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.company_name} (Kalan: {fmtTL(c.summary?.remaining_debt)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCompany && (
              <div className="text-xs text-[#6B7280] mt-1">
                Kalan borç: <b className="text-[#0B3676]">{fmtTL(selectedCompany.summary?.remaining_debt)}</b>
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Sipariş (Opsiyonel — boşsa genel ödeme)
            </label>
            <Select value={form.order_id} onValueChange={(v) => set("order_id", v)}>
              <SelectTrigger data-testid="payment-order"><SelectValue placeholder="Sipariş seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Genel Ödeme —</SelectItem>
                {orders.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.order_no} (Kalan: {fmtTL(o.remaining_amount)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Tutar (TL) <span className="text-[#DC2626]">*</span>
            </label>
            <Input data-testid="payment-amount" type="number" min="0.01" step="0.01"
                   value={form.payment_amount}
                   onChange={(e) => set("payment_amount", e.target.value)} required />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Dekont Tarihi <span className="text-[#DC2626]">*</span>
            </label>
            <Input data-testid="payment-receipt-date" type="date" value={form.receipt_date}
                   onChange={(e) => set("receipt_date", e.target.value)} required />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Ödeme Yöntemi <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
              <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(METHODS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Dekont No</label>
            <Input data-testid="payment-receipt-no" value={form.receipt_no}
                   onChange={(e) => set("receipt_no", e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Açıklama</label>
            <Textarea data-testid="payment-description" value={form.description}
                      onChange={(e) => set("description", e.target.value)} rows={2} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Dekont Eki (Opsiyonel - PDF veya görsel)
            </label>
            <FileUploader
              value={form.attachments || []}
              onChange={(v) => set("attachments", v)}
              category="payments"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              maxFiles={1}
              label="Dekont Yükle"
              testid="payment-attachment"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate("/cari")}>Vazgeç</Button>
        <Button type="submit" disabled={saving} data-testid="save-payment"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : "Ödemeyi Kaydet"}
        </Button>
      </div>
    </form>
  );
}
